import React, { useCallback, useState } from 'react';
import { Button, Card, Col, DatePicker, Row, Select, Space, Spin, Typography, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getUserPrimaryCompanyId, hasRole } from '../../../../utils/permissionUtils';
import CommercePivotTable from './components/CommercePivotTable';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const SalesOrderPivotReport = () => {
  const { t, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month').subtract(5, 'month'),
    dayjs().endOf('month')
  ]);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}), 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  React.useEffect(() => {
    if (!isTenantAdmin) return;
    fetchWithAuth('/api/commerce/companies-for-select')
      .then((r) => r.ok ? r.json() : [])
      .then((list) => setCompaniesForSelect(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [fetchWithAuth, isTenantAdmin]);

  const loadData = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      message.warning(t('reports.commerce.dateRange'));
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('dateFrom', dateRange[0].format('YYYY-MM-DD'));
      params.set('dateTo', dateRange[1].format('YYYY-MM-DD'));
      if (isTenantAdmin && companyFilter) params.set('companyId', companyFilter);
      const res = await fetchWithAuth(`/api/reports/commerce/sales-orders-flat?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setDataSource(Array.isArray(json?.items) ? json.items : []);
    } catch (err) {
      message.error(`${t('reports.commerce.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [dateRange, companyFilter, fetchWithAuth, isTenantAdmin, t]);

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} align="middle">
          {isTenantAdmin && (
            <Col span={6}>
              <Text strong>{t('reports.commerce.company')}</Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 4 }}
                placeholder={t('reports.commerce.selectCompany')}
                value={companyFilter}
                onChange={setCompanyFilter}
                options={companiesForSelect.map((c) => ({ label: c.name, value: c.id }))}
              />
            </Col>
          )}
          <Col span={10}>
            <Text strong>{t('reports.commerce.dateRange')}</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 4 }}
              value={dateRange}
              onChange={setDateRange}
            />
          </Col>
          <Col span={24}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={loadData} loading={loading}>
                {t('common.search') || '查詢'}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={loadData} disabled={loading} />
            </Space>
          </Col>
        </Row>
      </Card>
      <Spin spinning={loading}>
        <CommercePivotTable dataSource={dataSource} />
      </Spin>
    </div>
  );
};

export default SalesOrderPivotReport;
